<script>
import Loader from '../widgets/loader'
import _ from "lodash";
import TableBusyLoader from '../widgets/table-busy-loader'

export default {
  name: "ComponentModel",
  components: {
    Loader,
    TableBusyLoader,
  },
  props: {
    sectionTitle: String,
    cardTitle: String,
    loading: {type: Boolean, default: false},
    resourceRoute: String,
    modelVariable: String,
    modelKey: {type: String, default: 'id'},
    columns: {
      type: Object,
      default() {
        return {
          [this.modelVariable || 'model']: [
            {
              key: 'name',
              label: 'Name',
              sortable: true,
              tdClass: 'align-middle'
            }
          ]
        };
      }
    },
    cardAttrs: {
      type: Object,
      default() {
        return { title: null, subTitle: null }
      }
    },
    showPagination: {type: Boolean, default: true},
    page: {type: Number, default: 1},
    perPage: {type: Number, default: 10},
    from: {type: Number, default: 1},
    total: {type: Number, default: 0},
    search: {type: String, default: ''},
    sortBy: {type: String, default: ''},
    sortDesc: {type: Boolean, default: false},
    sortDirection: {type: String, default: 'asc'},
    enableActionButtons: {type: Boolean, default: true},
    denyCreate: {type: Boolean, default: false},
    denyExport: {type: Boolean, default: true},
    denyEmptyStateCreate: {type: Boolean, default: false},
    hideTableHeader: {type: Boolean, default: false},
    busy: {type: Boolean, default: false},
    modelsPaginated: {
      type: [Array, Function], default() {
        return [];
      }
    },
    tableProps: {
      type: Object, default() {
        return {}
      }
    },
    tableMinHeight: {type: Number},
    tableHeight: {type: Number, default: 530},
    filterParams: { type: Object, default: () => {} },
    actions: {
      type: Array,
      default: () => []
    },
    actionButtons: {
      type: Array,
      default: () => []
    },
    models: {
      type: Array,
      default: () => []
    },
    emptyStateTitle: {
      type: String,
      default: null
    },
    emptyStateSubtitle: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      currentPage: this.page,
      localModelsPaginated: [],
      debounceUpdateLocalModelsPaginated: this.lodash.debounce(this.updateLocalModelsPaginated, 250),
      localBusy: false
    }
  },
  computed: {
    actionOnLast() {
      const columns = (this.columns[this.modelVariable || 'model'] || [])
      return columns.findIndex((c) => c.key === 'action') === (columns.length - 1)
    },
    // Field keys for which a consumer supplied a custom `#head-<key>` slot. Only these get a
    // dynamic `head()` override on b-table, so every other table's default header rendering
    // (including sort icons) is left completely untouched.
    customHeaderFieldKeys() {
      const prefix = 'head-'
      return Object.keys(this.$scopedSlots)
          .filter(name => name.startsWith(prefix))
          .map(name => name.slice(prefix.length))
    }
  },
  watch: {
    modelsPaginated() {
      if (!(this.modelsPaginated instanceof Function)) return;
      this.updateLocalModelsPaginated();
    },
    search() {
      if (!(this.modelsPaginated instanceof Function)) return;
      this.debounceTableRefresh();
    },
  },
  methods: {
    debounceTableRefresh: _.debounce(function () {
      if (this.$refs.table) this.$refs.table.refresh()
    }, 250),
    setLocalModelsPaginated(items) {
      this.localModelsPaginated = items instanceof Array ? items.slice() : []
      this.localBusy = false
    },
    updateLocalModelsPaginated() {
      this.localBusy = true;
      this.$nextTick(() => {
        try {
          // Call provider function passing it the context and optional callback
          const data = this.modelsPaginated({ currentPage: this.page }, this.setLocalModelsPaginated)
          if (data instanceof Promise) {
            // Provider returned Promise
            data.then(items => {
              // Provider resolved with items
              this.setLocalModelsPaginated(items)
            })
          } else if (data instanceof Array) {
            // Provider returned Array data
            this.setLocalModelsPaginated(data)
          } else {
            /* istanbul ignore if */
            if (this.items.length !== 2) {
              this.localBusy = false
            }
          }
        } catch (e) /* istanbul ignore next */ {
          this.localBusy = false
        }
      })
    },
    emitCreateClick(event) {
      return this.$emit('create-click', event)
    },
    emitExportClick(event) {
      return this.$emit('export-click', event)
    }
  }
};
</script>

<template>
  <div>
    <h4 class="mb-2 font-size-14" v-if="sectionTitle">{{sectionTitle}}</h4>      
    <b-card v-bind="cardAttrs" :title="undefined" :sub-title="undefined">
      <b-card-title title-tag="div" class="clearfix" v-if="cardTitle">
        <slot name="card-header" v-bind="{ denyCreate, emitCreateClick, cardAttrs, resourceRoute }">
          <div class="float-end">
            <slot name="card-tools" v-bind="{ denyCreate, emitCreateClick }">
              <!-- additional actions from this.actions -->
              <span v-bind:key="key" v-for="(action, key) in actions">
                <component :is="action.component" v-bind="action"/>
              </span>
              <b-button
                  v-if="!denyExport"
                  @click="emitExportClick"
                  variant="success"
                  size="sm"
                  class="me-2"
              >{{ $t('Export') }}</b-button>
              <b-button
                  v-if="!denyCreate"
                  @click="emitCreateClick"
                  variant="primary"
                  size="sm"
              >{{ $t('Create New') }}</b-button>
            </slot>
          </div>
          <b-card-title v-if="cardTitle">{{ cardAttrs.title || `${$t('List')} ${cardTitle || lodash.startCase($t(resourceRoute + '.plural'))}` }}</b-card-title>
          <b-card-sub-title v-if="cardAttrs.subTitle">{{ cardAttrs.subTitle }}</b-card-sub-title>
        </slot>
      </b-card-title>
      <div v-if="!hideTableHeader" class="row mt-4">
        <slot name="table-header">
<!--          show per table-->
          <div class="col-sm-12 col-md-6">
            <div id="tickets-table_length" class="dataTables_length">
              <label class="d-inline-flex align-items-center">
                {{$t('show')}}&nbsp;
                <b-form-select
                    class="form-select-sm mx-2"
                    :value="perPage"
                    @change="$emit('update:perPage', $event)"
                    size="sm"
                    :options="[10, 50, 100, 250, 1000]"
                ></b-form-select
                >&nbsp;{{$t('entries')}}
              </label>
            </div>
          </div>
          <!-- Search -->
          <div class="col-sm-12 col-md-6">
            <div
                id="tickets-table_filter"
                class="dataTables_filter text-md-end"
            >
              <label class="d-inline-flex align-items-center">
                {{$t('Search')}}:
                <b-form-input
                    :value="search"
                    @update="$emit('update:search', $event)"
                    type="search"
                    :placeholder="$t('Search')+'...'"
                    class="form-control form-control-sm ms-2"
                    :debounce="500"
                ></b-form-input>
              </label>
            </div>
          </div>
          <!-- End search -->
        </slot>
      </div>
      <!--   Empty State   -->
      <div v-if="!loading && !busy && models.length === 0" class="empty-state">
        <div class="text-center">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M18.0236 12.0001C19.5863 12.0001 20.0416 12.013 20.4486 12.1107C20.8566 12.2087 21.2468 12.3703 21.6048 12.5896C21.9614 12.8082 22.2926 13.1211 23.3976 14.2261L24.5858 15.4144C24.9608 15.7894 25.4696 16.0001 26 16.0001H32.4C34.1132 16.0001 35.2778 16.0017 36.178 16.0752C37.0548 16.1469 37.5032 16.2767 37.816 16.4361C38.5686 16.8196 39.1806 17.4315 39.564 18.1842C39.7234 18.497 39.8532 18.9454 39.9248 19.8222C39.9984 20.7224 40 21.887 40 23.6002V28.4002C40 30.1132 39.9984 31.2778 39.9248 32.178C39.8532 33.0548 39.7234 33.5032 39.564 33.816C39.1806 34.5688 38.5686 35.1806 37.816 35.5642C37.5032 35.7236 37.0548 35.8534 36.178 35.925C35.2778 35.9986 34.1132 36.0002 32.4 36.0002H15.6C13.8868 36.0002 12.7223 35.9986 11.8221 35.925C10.9452 35.8534 10.4968 35.7236 10.184 35.5642C9.4314 35.1806 8.81946 34.5688 8.43598 33.816C8.2766 33.5032 8.14674 33.0548 8.0751 32.178C8.00156 31.2778 8 30.1132 8 28.4002V19.6001C8 17.887 8.00156 16.7224 8.0751 15.8222C8.14674 14.9454 8.2766 14.497 8.43598 14.1842C8.81946 13.4315 9.4314 12.8196 10.184 12.4361C10.4968 12.2767 10.9452 12.1469 11.8221 12.0752C12.7223 12.0017 13.8868 12.0001 15.6 12.0001H18.0236ZM18.2274 8.00006L18.2273 8.00006L18.0236 8.00012H15.5174L15.5172 8.00012C13.9074 8.0001 12.5787 8.00008 11.4963 8.08852C10.3722 8.18036 9.33874 8.3775 8.36808 8.87208C6.86278 9.63906 5.63894 10.8629 4.87194 12.3682C4.37736 13.3389 4.18024 14.3723 4.08838 15.4965C3.99997 16.5788 3.99998 17.9075 4 19.5173V19.5175V28.4828V28.483C3.99998 30.0927 3.99997 31.4215 4.08838 32.5038C4.18024 33.628 4.37736 34.6614 4.87194 35.632C5.63894 37.1374 6.86278 38.3612 8.36808 39.1282C9.33874 39.6228 10.3722 39.8198 11.4963 39.9118C12.5788 40.0002 13.9075 40.0002 15.5175 40.0002H32.4826C34.0926 40.0002 35.4212 40.0002 36.5036 39.9118C37.6278 39.8198 38.6612 39.6228 39.632 39.1282C41.1372 38.3612 42.361 37.1374 43.128 35.632C43.6226 34.6614 43.8198 33.628 43.9116 32.5038C44 31.4214 44 30.0926 44 28.4828V23.5174C44 21.9076 44 20.5788 43.9116 19.4965C43.8198 18.3723 43.6226 17.3389 43.128 16.3682C42.361 14.8629 41.1372 13.6391 39.632 12.8721C38.6612 12.3775 37.6278 12.1804 36.5036 12.0885C35.4212 12.0001 34.0927 12.0001 32.4828 12.0001H32.4826H26.8284L26.226 11.3977L26.082 11.2536L26.0818 11.2533C25.1843 10.355 24.5061 9.67622 23.6948 9.179C22.979 8.74038 22.1986 8.41714 21.3822 8.22116C20.4568 7.99901 19.4974 7.99946 18.2274 8.00006ZM20 25.0001C20 23.3433 21.3432 22.0001 23 22.0001C24.6568 22.0001 26 23.3433 26 25.0001C26 26.6569 24.6568 28.0001 23 28.0001C21.3432 28.0001 20 26.6569 20 25.0001ZM23 18.0001C19.134 18.0001 16 21.1341 16 25.0001C16 28.8661 19.134 32.0001 23 32.0001C24.2056 32.0001 25.3398 31.6953 26.3302 31.1587L28.5858 33.4143C29.3668 34.1953 30.6332 34.1953 31.4142 33.4143C32.1952 32.6333 32.1952 31.3669 31.4142 30.5859L29.1586 28.3303C29.6952 27.3399 30 26.2057 30 25.0001C30 21.1341 26.866 18.0001 23 18.0001Z" fill="#A3A3A3"/>
          </svg>
        </div>
        <div class="text-center">
          <div class="empty-state__title text-block-md">{{ emptyStateTitle || lodash.startCase($t('data_not_found')) }}</div>
          <div class="empty-state__subtitle text-block-sm">{{ emptyStateSubtitle || lodash.startCase($t('{something} list is empty', {something: $t(`${resourceRoute}.singular`)})) }}</div>
        </div>
        <slot name="emptyStateAction">
<!--          <div class="d-flex justify-content-center" v-if="!denyEmptyStateCreate">-->
<!--            <router-link :to="{name: `${resourceRoute}.create`}">-->
<!--              <b-btn variant="primary">{{lodash.startCase($t('create {something}', {something: $t(`${resourceRoute}.singular`)}))}}</b-btn>-->
<!--            </router-link>-->
<!--          </div>-->
        </slot>
      </div>
      <!-- Table -->
      <b-table
          v-show="busy || loading || models.length > 0"
          ref="table"
          :busy="busy"
          @update:busy="$emit('update:busy', $event)"
          :items="modelsPaginated"
          :fields="columns[modelVariable]"
          :current-page="page"
          :per-page="perPage"
          :filter="filterParams"
          :sort-by="sortBy"
          @update:sortBy="$emit('update:sortBy', $event)"
          :sort-desc="sortDesc"
          @update:sortDesc="$emit('update:sortDesc', $event)"
          show-empty
          @filtered="$emit('filtered', $event)"
          :responsive="true"
          :sticky-header="true"
          head-variant="light"
          thead-class="align-middle"
          :style="{ margin: 0, minHeight: `${tableMinHeight || tableHeight}px`, maxHeight: `${tableHeight}px` }"
          v-bind="tableProps"
          hover
          @row-clicked="!$event._denyShow && $emit('show-click', $event)"
      >
        <template #table-colgroup="{ fields }">
          <col v-for="field in fields" :key="field.key" v-bind="field">
        </template>
        <template v-for="key in customHeaderFieldKeys" #[`head(${key})`]="data">
          <slot :name="`head-${key}`" v-bind="data" />
        </template>
        <template #cell(index)="data">
          <b>{{ data.index + from }}</b>
        </template>
        <template #cell(action)="data">
          <b-dropdown
              v-if="!data.item['_denyShow'] || !data.item['_denyEdit'] || !data.item['_denyDelete']"
              class="card-drop"
              variant="white"
              toggle-class="p-0"
              :menu-class="{'dropdown-menu-end': actionOnLast}"
          >
            <template v-slot:button-content>
              <i class="mdi mdi-dots-horizontal"></i>
            </template>
            <slot name="action" v-bind="{ data }">
              <b-dropdown-item v-if="!data.item['_denyShow']" @click="$emit('show-click', data.item)">
                {{ $t('Detail') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item['_denyEdit']" @click="$emit('edit-click', data.item)">
                {{ $t('Edit') }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item['_denyDelete']" @click="$emit('delete-click', data.item)">
                <span class="text-danger">{{ $t('Delete') }}</span>
              </b-dropdown-item>
              <b-dropdown-item v-for="actionButton in actionButtons" :key="actionButton.key" @click="actionButton.action(data.item)">
                {{ actionButton.label }}
              </b-dropdown-item>
            </slot>
          </b-dropdown>
        </template>
        <template #cell()="data">
          <template v-if="data.field.component">
            <component :is="data.field.component" v-bind="{item: data.item}" v-on="data.field['componentListeners'] || {}"></component>
          </template>
          <template v-else>{{ data.value }}</template>
        </template>
        <template #empty="scope">
          <Loader :loading="busy" no-axios>
            <div class="card shadow-none rounded-0" style="margin: -0.75rem; background-color: #F5F6F7"
                 :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }">
              <div
                  style="position: absolute;
                  left: 50%;
                  top: 40%;
                  transform: translate(-50%, -50%);"
              >
                <div class="d-flex justify-content-center">
                  <img
                      src="@/assets/images/report_gmailerrorred_24px.png"
                      alt="not found"
                      style="height: 48px;width: 48px;">
                </div>
                <p class="table-status-text">{{ lodash.upperFirst($t('search_not_found')) }}</p>
              </div>
            </div>
          </Loader>
        </template>
        <template #emptyfiltered="scope">
          <Loader :loading="busy" no-axios>
            <div class="card shadow-none rounded-0" style="margin: -0.75rem; background-color: #F5F6F7"
                 :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }">
              <div
                  style="position: absolute;
                  left: 50%;
                  top: 40%;
                  transform: translate(-50%, -50%);"
              >
                <div class="d-flex justify-content-center">
                  <img
                      src="@/assets/images/report_gmailerrorred_24px.png"
                      alt="not found"
                      style="height: 48px;width: 48px;">
                </div>
                <p class="table-status-text">{{ lodash.upperFirst($t('search_not_found')) }}</p>
              </div>
            </div>
          </Loader>
        </template>
        <template #table-busy>
          <TableBusyLoader :loading="true" no-axios>
            <div
                class="card shadow-none rounded-0"
                style="margin: -0.75rem; background-color: #F5F6F7"
                :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }">
              <p
                  class="position-absolute table-status-text"
                  style="
                  height: 40px;
                  position: absolute;
                  left: 50%;
                  top: 30%;
                  transform: translate(-50%, -50%);
                  ">{{ lodash.upperFirst($t('fetch_data')) }}</p>
            </div>
          </TableBusyLoader>
        </template>
      </b-table>
      <div  v-if="total > perPage" class="row">
        <div class="col">
          <div
              class="dataTables_paginate paging_simple_numbers float-end"
          >
            <!-- pagination -->
            <b-pagination
                :key="`pagination-button-${total}-${perPage}`"
                :value="page"
                @change="$emit('update:page', $event)"
                :total-rows="total"
                :per-page="perPage"
                :disabled="busy"
                class="pagination pagination-rounded d-flex"
            ></b-pagination>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<style>
p.table-status-text {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}
  a.detail-button, a.edit-button, a.delete-button {
    padding: 0 5px;
    color: #343434;
    cursor: pointer;
  }

  a.detail-button:hover {
    color: #28a745
  }

  a.edit-button:hover {
    color: #ffc107;
  }

  a.delete-button:hover {
    color: #dc3545;
  }
</style>

<style scoped lang="scss">
  .empty-state {
    border: 2px dashed #A3A3A3;
    border-radius: 8px;
    display: flex;
    padding: 24px;
    gap: 24px;
    flex-direction: column;
    justify-content: center;
    &__subtitle {
      color: #667085;
    }
  }
</style>